<template>
  <vl-region :key="key">
    <vl-layout>
      <PbsGoBackLink />
      <template v-if="!loading">
        <vl-grid>
          <vl-column width="6" width-xs="12">
            <vl-title tag-name="h1">
              {{ $t('productprocessdefinition.detail.title') }} <PbsStatusIcon v-if="detail" :id="detail?.statusId" />
            </vl-title>
          </vl-column>
          <vl-column width="6" width-xs="12">
            <vl-action-group
              v-if="detail && !editStore.isEdit(ProductProcessDefinitionEditType.ProductProcessDefinition, detail.id)"
              mod-align-right
              mod-space
              between
            >
              <vl-button
                v-if="editStore.canEdit"
                icon="pencil"
                mod-icon-before
                mod-secondary
                :mod-disabled="editStore.editing"
                @click="editStore.openEdit(ProductProcessDefinitionEditType.ProductProcessDefinition, detail.id)"
              >
                {{ $t('general.edit') }}
              </vl-button>
              <DefinitionActionButton :detail="detail" :actions="actionButtonTypes" mod-large />
            </vl-action-group>
            <vl-action-group v-else mod-align-right mod-space between>
              <vl-button icon="save" :mod-loading="reloading" mod-icon-before @click="save">{{
                $t('general.save')
              }}</vl-button>
              <vl-button
                icon="cross"
                :mod-loading="loading"
                mod-icon-before
                mod-secondary
                mod-error
                @click="editStore.closeEdit"
              >
                {{ $t('general.cancel') }}
              </vl-button>
            </vl-action-group>
          </vl-column>
        </vl-grid>
        <vl-grid
          v-if="detail"
          :class="{
            edit: editStore.isEdit(ProductProcessDefinitionEditType.ProductProcessDefinition, detail.id),
            'vl-u-spacer-bottom--medium': true
          }"
        >
          <vl-column>
            <ProductProcessDefinitionEdit
              v-if="editStore.isEdit(ProductProcessDefinitionEditType.ProductProcessDefinition, detail.id)"
              ref="editForm"
            />
            <ProductProcessDefinitionRead v-else />
          </vl-column>
        </vl-grid>
        <template v-if="detail && !loading">
          <SourceSpecification :detail="detail" />
          <div class="vl-u-spacer--large vl-u-visible--s"></div>
          <ProductSpecification :detail="detail" />
          <div class="vl-u-spacer--large vl-u-visible--s"></div>
          <ProductTransformation />
          <template v-if="canEditDataMapping">
            <div class="vl-u-spacer--large vl-u-visible--s"></div>
            <ProductEntityDataMapping :detail="detail" />
          </template>
        </template>
      </template>
      <template v-if="loading">
        <vl-region>
          <div v-vl-align:center>
            <vl-loader :message="$t('productprocessdefinition.detail.loading')" />
          </div>
        </vl-region>
      </template>
    </vl-layout>
  </vl-region>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute, onBeforeRouteLeave } from 'vue-router';
import { useProductProcessDefinitionDetailStore } from '../../store/product-process-definition/product-process-definition-detail.store';
import { ProductProcessDefinitionDetailDto } from '@/api/backoffice/clients';
import ProductProcessDefinitionRead from './ProductProcessDefinitionRead.vue';
import {
  ProductProcessDefinitionEditType,
  useProductProcessDefinitionEditStore
} from '../../store/product-process-definition/product-process-definition-edit.store';
import { useAlertStore } from '@/modules/core/store/alert.store';
import ProductProcessDefinitionEdit from './ProductProcessDefinitionEdit.vue';
import SourceSpecification from './subcomponents/source-specification/SourceSpecification.vue';
import ProductSpecification from './subcomponents/product-specification/ProductSpecification.vue';
import ProductTransformation from './subcomponents/product-transformation/ProductTransformation.vue';
import ProductEntityDataMapping from './subcomponents/product-entity-data/ProductEntityDataMapping.vue';
import DefinitionActionButton from './subcomponents/definition-action-button/DefinitionActionButton.vue';
import PbsGoBackLink from '@/components/PbsGoBackLink.vue';
import PbsStatusIcon from '@/components/PbsStatusIcon.vue';
import { ProductProcessDefinitionActionButtonType } from '../../common/product-process-definition.common';

const store = useProductProcessDefinitionDetailStore();
const editStore = useProductProcessDefinitionEditStore();

const route = useRoute();

const editForm = ref();

const key = ref(route.params.id);

watch(
  () => route.params.id,
  (newKey) => {
    if (key.value !== newKey) {
      key.value = newKey;
      getDetail();
    }
  }
);

const detail = computed((): ProductProcessDefinitionDetailDto => {
  return store.detail;
});

const actionButtonTypes = computed(() => {
  return [
    ProductProcessDefinitionActionButtonType.ChangeStatus,
    ProductProcessDefinitionActionButtonType.DryRun,
    ProductProcessDefinitionActionButtonType.Export,
    ProductProcessDefinitionActionButtonType.GenerateReport,
    ProductProcessDefinitionActionButtonType.Duplicate,
    ProductProcessDefinitionActionButtonType.Dependencies,
    ProductProcessDefinitionActionButtonType.CascadeDelete
  ];
});

const parentDefinitionId = computed((): number => {
  return detail.value?.parent?.id;
});

const loading = computed((): boolean => {
  return store.loading;
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const canEditDataMapping = computed((): boolean => {
  return editStore.canEdit && parentDefinitionId.value > 0;
});

const id = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

onMounted(async () => {
  await getDetail();
});

const save = () => {
  editForm.value.onSubmit();
};

const getDetail = async () => {
  editStore.init();
  useAlertStore().clearAlert();
  await store.get(id.value);
};

onBeforeRouteLeave((to, from, next) => {
  const id = parseInt(from.params.id as string, 10);
  if (id && store) {
    store.clear(id);
    editStore.closeEdit();
  }
  next();
});
</script>
